// BR 巴西逻辑

import vueI18n from '@/i18n/locale'
import caculateWidthBy10FontSize from '../caculateFontSize.js'
import { getMaxValue, getMaxLengthValue, isSmallScreenMobile } from '../utils.js'
import {
  LegendBuilder,
  XAxisBuilder,
  YAxisBuilder,
  WeekendBuilder,
  ToolTipBuilder,
  getEchartHeightAndBottomByLocale,
} from '../builderBR.js'
import { isEmpty } from 'lodash'

const durationCfg = getEchartHeightAndBottomByLocale('serviceDuration')
const serviceDurationBottom = durationCfg.bottom
const serviceDurationBottomHor = durationCfg.bottomHor

const timeoutCfg = getEchartHeightAndBottomByLocale('serviceTimeout')
const serviceTimeoutBottom = timeoutCfg.bottom
const serviceTimeoutBottomHor = timeoutCfg.bottomHor

const cancelCfg = getEchartHeightAndBottomByLocale('serviceCancel')
const serviceCancelBottom = cancelCfg.bottom
const serviceCancelBottomHor = cancelCfg.bottomHor

const questionCfg = getEchartHeightAndBottomByLocale('serviceQuestion')
const serviceQuestionBottom = questionCfg.bottom
const serviceQuestionBottomHor = questionCfg.bottomHor

// ------------------------------
// Legend / Grid 布局辅助（解决 legend 压到图表上的问题）
// 说明：ECharts 的 legend 不会自动“挤开” grid，需要我们为 legend 预留 bottom 空间。
// 这里使用“按容器宽度估算 legend 换行行数”的方式，动态增加 grid.bottom。
// 注意：目前 transformer 未传入容器真实宽度，因此使用经验值做估算；
// 若后续可拿到容器 clientWidth，建议将 width 作为参数传入以获得更精确的结果。
// ------------------------------

// legend 每一项的粗略布局参数（与 LegendBuilder 的默认样式保持一致的体验即可）
const LEGEND_ICON_WIDTH = 10
const LEGEND_ICON_GAP = 10
const LEGEND_ITEM_GAP = 12
const LEGEND_PADDING_LR = 16
const LEGEND_LINE_HEIGHT = 20 // 每行 legend 的占用高度（包含上下间距）

/**
 * 获取图表容器宽度的“估算值”。
 * - 这里用于 legend 自动换行行数的估算，避免 legend 覆盖图表。
 * - 若后续能拿到真实容器宽度（例如 this.$el.clientWidth），应优先用真实宽度替代。
 */
function getApproxChartWidth(direction) {
  // 小屏竖屏通常更窄，给一个更保守的宽度
  if (direction === 'vertical') return isNarrowViewport() ? 320 : 343
  // 横屏/Pad 场景更宽
  return isNarrowViewport() ? 568 : 667
}

/**
 * 估算 legend 需要的行数（labels 会按宽度自动换行）。
 * @param {string[]} labels - legend 文案集合
 * @param {number} chartWidth - 容器宽度（可为估算值）
 */
function calcLegendRows(labels = [], chartWidth) {
  const available = Math.max(chartWidth - LEGEND_PADDING_LR * 2, 1)
  let rows = 1
  let rowWidth = 0

  labels.forEach(text => {
    // 使用项目内的宽度估算工具（fontSize 默认为 10，这里显式传入 12 更接近 legend 文案）
    const textWidth = caculateWidthBy10FontSize(text)
    const itemWidth = LEGEND_ICON_WIDTH + LEGEND_ICON_GAP + textWidth + LEGEND_ITEM_GAP
    // 超过当前行可用宽度则换行
    if (rowWidth + itemWidth > available) {
      rows += 1
      rowWidth = itemWidth
    } else {
      rowWidth += itemWidth
    }
  })

  return rows
}

// 图表高度是否需要动态补偿
function isNarrowViewport(threshold = 430) {
  return window.innerWidth <= threshold
}

/**
 * 计算「取消原因」图表在小屏场景下建议额外增加的高度。
 * 背景：当 legend 换行较多时，虽然我们通过动态 grid.bottom 预留了 legend 空间，
 * 但整体 chartHeight 仍然固定，导致“绘图区”被压缩得过小（柱子/坐标轴/tooltip 显示不够）。
 * 因此在小屏下，按 legend 的换行行数，给取消原因图表额外增加一些高度。
 *
 * @param {object} chartData - 后台返回的 chartData
 * @param {'vertical'|'horizontal'} direction - 竖屏/横屏
 * @returns {number} 需要追加的高度（px）
 */
function getCancelReasonExtraHeight(chartData = {}, direction) {
  // 非小屏不额外加高，避免其它端出现大量空白
  if (!isNarrowViewport()) return 0

  // 仅在竖屏更容易“挤”，横屏一般宽度更大，可按需开启
  if (direction !== 'vertical') return 0

  // 与 parseCancelReasonChartsData 内保持一致：仅统计“有效展示”的原因
  const reasonFields = [
    'dishSoldOut',
    'storeClosed',
    'dishNotServeTooBusy',
    'dishNotServeUnexpectedReason',
    'cDutyCancel',
    'riderNeverBeenStore',
    'otherQuestion',
    'noRiderInStore',
    'menuNeedUpdate',
    'orderInDangerArea',
    'orderBeyondDeliveryRange',
    'prankOrder',
    'questionAboutActivitiesExpenses',
    'orderMissedTimeout',
    'selfDeliveryTimeout',
    'otherBDutyCancelOrder',
  ]

  // labels 使用 i18n 的实际展示文案（pt-BR 文案更长，换行风险更大）
  const fieldToLabel = {
    dishSoldOut: vueI18n.t('FoodB_scenario_tTcW_glIK'),
    storeClosed: vueI18n.t('FoodB_scenario_TNIB_LZav'),
    dishNotServeTooBusy: vueI18n.t('FoodB_scenario_FkIm_wbyt'),
    dishNotServeUnexpectedReason: vueI18n.t('FoodB_scenario_ShEY_kNaS'),
    cDutyCancel: vueI18n.t('FoodB_scenario_HbKA_WhYt'),
    riderNeverBeenStore: vueI18n.t('FoodB_scenario_cPkp_BcjZ'),
    otherQuestion: vueI18n.t('FoodB_scenario_LpfM_MGER'),
    noRiderInStore: vueI18n.t('FoodB_scenario_psNp_kSMI'),
    menuNeedUpdate: vueI18n.t('FoodB_scenario_czrv_MZIJ'),
    orderInDangerArea: vueI18n.t('FoodB_scenario_YcnE_hnLK'),
    orderBeyondDeliveryRange: vueI18n.t('FoodB_scenario_iTKN_qRYT'),
    prankOrder: vueI18n.t('FoodB_scenario_FmAJ_xFEY'),
    questionAboutActivitiesExpenses: vueI18n.t('FoodB_scenario_Essn_oVjW'),
    orderMissedTimeout: vueI18n.t('FoodB_scenario_tWmb_Krny'),
    selfDeliveryTimeout: vueI18n.t('FoodB_scenario_UTGm_CSqz'),
    otherBDutyCancelOrder: vueI18n.t('FoodB_scenario_PuLv_FhUL'),
  }
  const enabledLabels = reasonFields
    .filter(field => {
      const raw = chartData?.[field] || []
      if (!raw || raw.length === 0) return false
      return raw.some(item => {
        const v = Number(item?.value ?? 0)
        return !Number.isNaN(v) && v !== 0
      })
    })
    .map(field => fieldToLabel[field])

  if (!enabledLabels.length) return 0

  const approxChartWidth = getApproxChartWidth(direction)
  const legendRows = calcLegendRows(enabledLabels, approxChartWidth)
  // legend 只有 4 行时不需要额外加高；每多 1 行，增加一行高度 + 少量缓冲
  if (legendRows <= 4) return 0

  return (legendRows - 4) * LEGEND_LINE_HEIGHT + 12
}

export default function transformer(
  data = {},
  dateToWeek,
  key = 'service',
  direction,
  isFullIndicator = true, // 是否展示Service(服务版块)全部Tab
) {
  const verticalHeight = isSmallScreenMobile()
    ? 220 // 小屏（竖屏）
    : 220
  const horizontalHeight = isSmallScreenMobile()
    ? 160 // 小屏（横屏）
    : 200
  const baseHeight = direction === 'vertical' ? verticalHeight : horizontalHeight
  // 动态高度：取消原因在小屏竖屏 + legend 多行时，固定高度会导致绘图区过小。
  // 这里将“Service 模块的整体 chartHeight”提升到能够覆盖最“挤”的那张图（取消原因）。
  const cancelReasonExtraHeight = getCancelReasonExtraHeight(data.chartData, direction)
  const chartHeight = baseHeight + cancelReasonExtraHeight

  const temp = isFullIndicator
    ? [
        parseDurationChartsData(data.chartData, dateToWeek, direction, chartHeight), // 营业时长
        parseCancelReasonChartsData(data.chartData, dateToWeek, direction, chartHeight), // 取消订单
        parseServiceChartsData(data.chartData, dateToWeek, direction, chartHeight), // 问题订单
        parseTimeoutChartsData(data.chartData, dateToWeek, direction, chartHeight), // 出餐超时
      ]
    : [
        parseCancelReasonChartsData(data.chartData, dateToWeek, direction, chartHeight), // 取消订单
        parseServiceChartsData(data.chartData, dateToWeek, direction, chartHeight), // 问题订单
        parseTimeoutChartsData(data.chartData, dateToWeek, direction, chartHeight), // 出餐超时
      ]
  const chartData = temp.filter(chart => {
    return chart.isShow
  })
  console.log(' service transformer chartData', temp)
  return {
    title: vueI18n.t('Gattaran_V1__hUDQ' /** 服务 */),
    ...data,
    chartData,
    chartHeight,
    chartkey: key,
  }
}

// 营业时长
function parseDurationChartsData(chartData = {}, dateToWeek, direction, chartHeight) {
  // console.log('parse: parseDurationChartsData', chartData)
  const {
    onlineDuration = [], // 日均在线时长
    onlineRatio = [], // 在线率
    peakOnlineDuration = [], // 高峰在线时长
    peakOnlineRatio = [], // 高峰在线率
  } = chartData || {}
  console.log(' 营业时长 chartData', chartData)
  const max = getMaxValue({ onlineDuration })
  const maxConnectivity = getMaxLengthValue({ onlineRatio })
  const xAxisData = onlineDuration.map(item => item.date)
  const left = (max ? caculateWidthBy10FontSize(max) + 8 : 16) + 26
  const right =
    (maxConnectivity
      ? caculateWidthBy10FontSize(maxConnectivity + '%')
      : caculateWidthBy10FontSize(0, 5)) + 10

  return {
    isShow: [onlineDuration, onlineRatio, peakOnlineDuration, peakOnlineRatio].some(
      i => !isEmpty(i),
    ),
    grid: {
      top: 26,
      left,
      right,
      bottom: direction === 'vertical' ? serviceDurationBottom : serviceDurationBottomHor,
    },
    tooltip: ToolTipBuilder({
      position: { left: 0, bottom: chartHeight },
    }),
    legend: LegendBuilder([
      vueI18n.t('Gattaran_opt_xawD_vBtI' /** 日均在线时长 */),
      {
        name: vueI18n.t('Gattaran_ES_ZfMz_IKyd' /** 在线率 */),
        icon: 'path://M960 470.857143H64c-5.028571 0-9.142857 4.114286-9.142857 9.142857v64c0 5.028571 4.114286 9.142857 9.142857 9.142857h896c5.028571 0 9.142857-4.114286 9.142857-9.142857v-64c0-5.028571-4.114286-9.142857-9.142857-9.142857z',
      },
      vueI18n.t('FoodB_requirement_aAaC_nmQK' /** 高峰在线时长 */),
      {
        name: vueI18n.t('FoodB_Side_YfKV_vTcN' /** 高峰时段在线率 */),
        icon: 'path://M960 470.857143H64c-5.028571 0-9.142857 4.114286-9.142857 9.142857v64c0 5.028571 4.114286 9.142857 9.142857 9.142857h896c5.028571 0 9.142857-4.114286 9.142857-9.142857v-64c0-5.028571-4.114286-9.142857-9.142857-9.142857z',
      },
    ]),
    xAxis: [
      XAxisBuilder({
        data: xAxisData.map(item => item.substring(5)),
      }),
    ],
    yAxis: [
      YAxisBuilder({
        name: vueI18n.t('Gattaran_opt_JcLo_plhe' /** 在线时长 */),
        nameTextStyle: {
          color: '#8D9099',
          fontSize: 10,
          align: 'left',
          padding: [0, 0, 0, -left + 8],
        },
      }),
      YAxisBuilder({
        name: vueI18n.t('Gattaran_ES_ZfMz_IKyd' /** 在线率 */) + '%',
        nameTextStyle: {
          color: '#8D9099',
          fontSize: 10,
          align: 'right',
          padding: [0, -right + 8, 0, 0],
        },
        axisLabel: {
          formatter: '{value}%',
          fontSize: 10,
          color: '#8D9099',
        },
        splitLine: {
          show: false,
        },
      }),
    ],
    series: [
      {
        name: vueI18n.t('Gattaran_opt_xawD_vBtI' /** 日均在线时长 */),
        type: 'bar',
        barWidth: 4,
        color: '#8D9099',
        stack: 'test',
        data: onlineDuration.map(item => item.value),
        silent: true,
      },
      {
        name: vueI18n.t('Gattaran_ES_ZfMz_IKyd' /** 在线率 */),
        type: 'line',
        showSymbol: false,
        yAxisIndex: 1,
        color: '#FFC9AD',
        lineStyle: {
          width: 1.5,
        },
        data: onlineRatio.map(item => item.value),
      },
      {
        name: vueI18n.t('FoodB_requirement_aAaC_nmQK' /** 高峰在线时长 */),
        type: 'bar',
        barWidth: 4,
        color: '#F8AD21',
        stack: 'test',
        data: peakOnlineDuration.map(item => item.value),
        silent: true,
      },
      {
        name: vueI18n.t('FoodB_Side_YfKV_vTcN' /** 高峰时段在线率 */),
        type: 'line',
        showSymbol: false,
        yAxisIndex: 1,
        color: '#F76A31',
        lineStyle: {
          width: 1.5,
        },
        data: peakOnlineRatio.map(item => item.value),
      },
      WeekendBuilder(xAxisData, dateToWeek),
    ],
  }
}

// 出餐超时
function parseTimeoutChartsData(chartData = {}, dateToWeek, direction, chartHeight) {
  console.log('parse: parseTimeoutChartsData', chartData)
  // 出餐超时订单数、出餐超时率
  const { deliveryOrdersCntRadio = [], deliveryOrdersCntRate = [] } = chartData || {}
  const max = getMaxValue({ deliveryOrdersCntRadio })
  const maxConnectivity = getMaxLengthValue({ deliveryOrdersCntRate })
  const xAxisData = deliveryOrdersCntRadio.map(item => item.date)
  const left = (max ? caculateWidthBy10FontSize(max) + 8 : 16) + 8
  const right =
    (maxConnectivity
      ? caculateWidthBy10FontSize(maxConnectivity + '%')
      : caculateWidthBy10FontSize(0, 5)) + 10

  return {
    isShow: [deliveryOrdersCntRadio, deliveryOrdersCntRate].some(i => !isEmpty(i)),
    grid: {
      top: 26,
      left,
      right,
      bottom: direction === 'vertical' ? serviceTimeoutBottom : serviceTimeoutBottomHor,
    },
    tooltip: ToolTipBuilder({
      position: { left: 0, bottom: chartHeight },
    }),
    legend: LegendBuilder([
      vueI18n.t('FoodB_requirement_puhp_VPIQ' /** 出餐超时订单数 */),
      {
        name: vueI18n.t('FoodB_requirement_uBiP_YocR' /** 出餐超时率 */),
        icon: 'path://M960 470.857143H64c-5.028571 0-9.142857 4.114286-9.142857 9.142857v64c0 5.028571 4.114286 9.142857 9.142857 9.142857h896c5.028571 0 9.142857-4.114286 9.142857-9.142857v-64c0-5.028571-4.114286-9.142857-9.142857-9.142857z',
      },
    ]),
    xAxis: [
      XAxisBuilder({
        data: xAxisData.map(item => item.substring(5)),
      }),
    ],
    yAxis: [
      YAxisBuilder({
        // name: vueI18n.t('FoodB_requirement_puhp_VPIQ' /** 出餐超时订单数 */),
        nameTextStyle: {
          color: '#8D9099',
          fontSize: 10,
          align: 'left',
          padding: [0, 0, 0, -left + 8],
        },
      }),
      YAxisBuilder({
        // name: vueI18n.t('FoodB_requirement_uBiP_YocR' /** 出餐超时率 */) + '%',
        nameTextStyle: {
          color: '#8D9099',
          fontSize: 10,
          align: 'right',
          padding: [0, -right + 8, 0, 0],
        },
        axisLabel: {
          formatter: '{value}%',
          fontSize: 10,
          color: '#8D9099',
        },
        splitLine: {
          show: false,
        },
      }),
    ],
    series: [
      {
        name: vueI18n.t('FoodB_requirement_puhp_VPIQ' /** 出餐超时订单数 */),
        type: 'bar',
        barWidth: 4,
        color: '#F76A31',
        stack: 'test',
        data: deliveryOrdersCntRadio.map(item => item.value ?? '--'),
        silent: true,
      },
      {
        name: vueI18n.t('FoodB_requirement_uBiP_YocR' /** 出餐超时率 */),
        type: 'line',
        showSymbol: false,
        yAxisIndex: 1,
        color: '#5BD4AB',
        lineStyle: {
          width: 1.5,
        },
        data: deliveryOrdersCntRate.map(item => item.value ?? '--'),
      },
      WeekendBuilder(xAxisData, dateToWeek),
    ],
  }
}

/**
 * 取消订单原因分类图表（按原因细分）
 * 当某一个对应字段有值才显示对应的原因分类数量，否则不显示
 */
function parseCancelReasonChartsData(chartData = {}, dateToWeek, direction, chartHeight) {
  // 一共 16 个取消原因，对应后台字段 + 文案 + 颜色
  const reasonDefs = [
    {
      field: 'dishSoldOut',
      label: vueI18n.t('FoodB_scenario_tTcW_glIK' /** 菜品售罄 */),
      color: '#F8AD21',
    },
    {
      field: 'storeClosed',
      label: vueI18n.t('FoodB_scenario_TNIB_LZav' /** 店铺已打烊 */),
      color: '#5BD4AB',
    },
    {
      field: 'dishNotServeTooBusy',
      label: vueI18n.t('FoodB_scenario_FkIm_wbyt' /** 店铺太忙无法出餐 */),
      color: '#FFC9AD',
    },
    {
      field: 'dishNotServeUnexpectedReason',
      label: vueI18n.t('FoodB_scenario_ShEY_kNaS' /** 因停水/停电等意外无法出餐 */),
      color: '#FF8D5C',
    },
    {
      field: 'cDutyCancel',
      label: vueI18n.t('FoodB_scenario_HbKA_WhYt' /** 顾客原因取消 */),
      color: '#4A9DFC',
    },
    {
      field: 'riderNeverBeenStore',
      label: vueI18n.t('FoodB_scenario_cPkp_BcjZ' /** 骑手从未到店 */),
      color: '#FF6A3C',
    },
    {
      field: 'otherQuestion',
      label: vueI18n.t('FoodB_scenario_LpfM_MGER' /** 其他问题 */),
      color: '#09BA78',
    },
    {
      field: 'noRiderInStore',
      label: vueI18n.t('FoodB_scenario_psNp_kSMI' /** 门店无骑手 */),
      color: '#B6B8BF',
    },
    {
      field: 'menuNeedUpdate',
      label: vueI18n.t('FoodB_scenario_czrv_MZIJ' /** 菜单需要更新 */),
      color: '#A87CF9',
    },
    {
      field: 'orderInDangerArea',
      label: vueI18n.t('FoodB_scenario_YcnE_hnLK' /** 订单地址位于危险区域 */),
      color: '#2FB4FF',
    },
    {
      field: 'orderBeyondDeliveryRange',
      label: vueI18n.t('FoodB_scenario_iTKN_qRYT' /** 订单超出配送范围 */),
      color: '#FFB347',
    },
    {
      field: 'prankOrder',
      label: vueI18n.t('FoodB_scenario_FmAJ_xFEY' /** 疑似诈骗或恶作剧 */),
      color: '#FF4D4D',
    },
    {
      field: 'questionAboutActivitiesExpenses',
      label: vueI18n.t('FoodB_scenario_Essn_oVjW' /** 对营销活动/费项有疑问 */),
      color: '#00BFA5',
    },
    {
      field: 'orderMissedTimeout',
      label: vueI18n.t('FoodB_scenario_tWmb_Krny' /** 超时未接 */),
      color: '#4A90E2',
    },
    {
      field: 'selfDeliveryTimeout',
      label: vueI18n.t('FoodB_scenario_UTGm_CSqz' /** 自配送履约超时 */),
      color: '#7ED321',
    },
    {
      field: 'otherBDutyCancelOrder',
      label: vueI18n.t('FoodB_scenario_PuLv_FhUL' /** 其他方发起的B责取消 */),
      color: '#8D9099',
    },
  ]

  // 过滤出有数据的原因
  const enabledReasons = reasonDefs
    .map(def => {
      const raw = chartData?.[def.field] || []
      return {
        ...def,
        raw,
        data: raw.map(item => item.value),
      }
    })
    .filter(def => {
      // 数组本身为空：不展示该原因
      if (!def.raw || def.raw.length === 0) return false

      // 需要至少有一天的 value 非 0，才认为有“有效数据”
      return def.raw.some(item => {
        const v = Number(item?.value ?? 0)
        return !Number.isNaN(v) && v !== 0
      })
    })

  // 没有任何原因有数据就不展示图表
  if (!enabledReasons.length) {
    return {
      isShow: false,
      grid: { top: 26, left: 16, right: 16, bottom: serviceCancelBottom },
      tooltip: ToolTipBuilder({
        position: { left: 0, bottom: chartHeight },
      }),
      legend: LegendBuilder([]),
      xAxis: XAxisBuilder({ data: [] }),
      yAxis: [],
      series: [],
    }
  }

  // 计算最大值用于 left padding
  const max = getMaxValue(Object.fromEntries(enabledReasons.map(def => [def.field, def.raw])))

  // x 轴直接用第一个有数据的原因的日期
  const xSource = enabledReasons[0].raw
  const xAxisData = xSource.map(item => item.date)

  const left = (max ? caculateWidthBy10FontSize(max) + 8 : 16) + 8
  const right = caculateWidthBy10FontSize(0, 5) + 10

  // ------------------------------
  // 关键修复：为 legend 预留空间，避免 legend 覆盖柱状图区域
  // - legend 文案在 pt-BR 场景下更长，容易自动换行成多行
  // - ECharts 不会自动调整 grid.bottom，因此需要我们动态增加 bottom
  // ------------------------------
  const legendLabels = enabledReasons.map(def => def.label)
  const approxChartWidth = getApproxChartWidth(direction)
  const legendRows = calcLegendRows(legendLabels, approxChartWidth)
  // 预留 legend 高度 + 少量缓冲，避免边缘压线
  let legendHeight = 0
  if (legendRows > 4) {
    legendHeight = (legendRows - 4) * LEGEND_LINE_HEIGHT + 12
  }
  // 取 locale 配置中的基础 bottom，再叠加 legend 的高度
  const baseBottom = direction === 'vertical' ? serviceCancelBottom : serviceCancelBottomHor
  const dynamicBottom = baseBottom + legendHeight

  return {
    isShow: true,
    grid: {
      top: 26,
      left,
      right,
      bottom: dynamicBottom,
    },
    tooltip: ToolTipBuilder({
      position: { left: 0, bottom: chartHeight },
    }),
    legend: LegendBuilder(enabledReasons.map(def => def.label)),
    xAxis: XAxisBuilder({
      data: xAxisData.map(item => item.substring(5)),
    }),
    yAxis: [
      YAxisBuilder({
        name: vueI18n.t('FoodOpB_10_Order_volume_WNmq' /** 订单数 */),
        nameTextStyle: {
          color: '#8D9099',
          fontSize: 10,
          align: 'left',
          padding: [0, 0, 0, -left + 8],
        },
      }),
    ],
    series: [
      ...enabledReasons.map(def => ({
        name: def.label,
        type: 'bar',
        barWidth: 4,
        color: def.color,
        stack: 'serviceCancelReason',
        data: def.data,
        silent: true,
      })),
      WeekendBuilder(xAxisData, dateToWeek),
    ],
  }
}

// 问题订单
function parseServiceChartsData(chartData = {}, dateToWeek, direction, chartHeight) {
  const {
    missingItemOrdersNum = [],
    smallPortionsOrdersNum = [],
    differentThanExpectedOrdersNum = [],
    wrongItemOrdersNum = [],
    imperfectOrderRatio = [],
  } = chartData || {}
  const max = getMaxValue({
    missingItemOrdersNum,
    smallPortionsOrdersNum,
    differentThanExpectedOrdersNum,
    wrongItemOrdersNum,
  })
  const maxRadio = getMaxLengthValue({ imperfectOrderRatio })
  const xAxisData = missingItemOrdersNum.map(item => item.date)
  const left = (max ? caculateWidthBy10FontSize(max) + 8 : 16) + 8
  const right =
    (maxRadio ? caculateWidthBy10FontSize(maxRadio + '%') : caculateWidthBy10FontSize(0, 5)) + 10

  return {
    isShow: [
      missingItemOrdersNum,
      smallPortionsOrdersNum,
      differentThanExpectedOrdersNum,
      wrongItemOrdersNum,
      imperfectOrderRatio,
    ].some(i => !isEmpty(i)),
    grid: {
      top: 26,
      left,
      right,
      bottom: direction === 'vertical' ? serviceQuestionBottom : serviceQuestionBottomHor,
    },
    tooltip: ToolTipBuilder({
      position: { left: 0, bottom: chartHeight },
    }),
    legend: LegendBuilder([
      vueI18n.t('FoodC_revision_Less_meal_pHwq' /** 少餐反馈 */),
      vueI18n.t('FoodC_improve__ovBF' /** 份量少 */),
      vueI18n.t('FoodC_improve__ByLg' /** 与预期不符 */),
      vueI18n.t('CS_Aftersale_Wrong_meal_gNCe' /** Completed Orders */),
      {
        name: vueI18n.t('Gattaran_opt2_egxV_xfoW' /** 问题订单率 */),
        icon: 'path://M960 470.857143H64c-5.028571 0-9.142857 4.114286-9.142857 9.142857v64c0 5.028571 4.114286 9.142857 9.142857 9.142857h896c5.028571 0 9.142857-4.114286 9.142857-9.142857v-64c0-5.028571-4.114286-9.142857-9.142857-9.142857z',
      },
    ]),
    xAxis: XAxisBuilder({ data: xAxisData.map(item => item.substring(5)) }),
    yAxis: [
      YAxisBuilder({
        name: vueI18n.t('FoodOpB_10_Order_volume_WNmq' /** 订单数 */),
        nameTextStyle: {
          color: '#8D9099',
          fontSize: 10,
          align: 'left',
          padding: [0, 0, 0, -left + 8],
        },
      }),
      YAxisBuilder({
        name: vueI18n.t('Gattaran_opt2_egxV_xfoW' /** 差评率% */),
        nameTextStyle: {
          color: '#8D9099',
          fontSize: 10,
          align: 'right',
          padding: [0, -right + 8, 0, 0],
        },
        axisLabel: {
          formatter: '{value}%',
          fontSize: 10,
          color: '#8D9099',
        },
        splitLine: {
          show: false,
        },
      }),
    ],
    series: [
      {
        name: vueI18n.t('FoodC_revision_Less_meal_pHwq' /** Negatively Rated Orders */),
        type: 'bar',
        barWidth: 4,
        color: '#FF8D5C',
        stack: 'service',
        data: missingItemOrdersNum.map(item => item.value),
        silent: true,
      },
      {
        name: vueI18n.t('FoodC_improve__ovBF' /** Completed Orders */),
        type: 'bar',
        barWidth: 4,
        color: '#F8AD21',
        stack: 'service',
        data: smallPortionsOrdersNum.map(item => item.value),
        silent: true,
      },
      {
        name: vueI18n.t('FoodC_improve__ByLg' /** Completed Orders */),
        type: 'bar',
        barWidth: 4,
        color: '#FFC9AD',
        stack: 'service',
        data: differentThanExpectedOrdersNum.map(item => item.value),
        silent: true,
      },
      {
        name: vueI18n.t('CS_Aftersale_Wrong_meal_gNCe' /** Completed Orders */),
        type: 'bar',
        barWidth: 4,
        color: '#D14F1F',
        stack: 'service',
        data: wrongItemOrdersNum.map(item => item.value),
        silent: true,
      },
      {
        name: vueI18n.t('Gattaran_opt2_egxV_xfoW' /** 问题订单率 */),
        type: 'line',
        yAxisIndex: 1,
        showSymbol: false,
        color: '#09BA78',
        data: imperfectOrderRatio.map(item => item.value),
        silent: true,
      },
      WeekendBuilder(xAxisData, dateToWeek),
    ],
  }
}
